/**
 * IndustrializationPane — 4-step ICFA Guided Pipeline right pane.
 *
 * Steps: 1 Brief | 2 BoM | 3 Make-or-Buy | 4 Sourcing Risk Register
 * Each generation step requires an explicit button click with cost estimate shown first.
 * All LLM-generated items carry a "to verify" badge.
 *
 * Sub-components live in ./industrialization/ for maintainability.
 */
import React, { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIndustrializationStore } from '../stores/industrializationStore';
import { StepStatusDot } from './industrialization/shared';
import BriefStep from './industrialization/BriefStep';
import BomStep from './industrialization/BomStep';
import MakeOrBuyStep from './industrialization/MakeOrBuyStep';
import RiskRegisterStep from './industrialization/RiskRegisterStep';

interface Props {
  subProjectId: string;
  solutionId: string;
}

type StepId = 'step1' | 'step2' | 'step3' | 'step4';

const STEPS: Array<{ id: StepId; label: string }> = [
  { id: 'step1', label: 'Brief' },
  { id: 'step2', label: 'BoM' },
  { id: 'step3', label: 'Make-or-Buy' },
  { id: 'step4', label: 'Risk Register' },
];

const IndustrializationPane: React.FC<Props> = ({ subProjectId, solutionId }) => {
  const { i18n } = useTranslation();
  const {
    dossier, costEstimate, isLoading, isRunningStep, currentStep, error,
    loadDossier, estimateCost, runStep,
  } = useIndustrializationStore();

  const [activeStep, setActiveStep] = useState(0);
  const [showCostConfirm, setShowCostConfirm] = useState<StepId | null>(null);

  const language = i18n.language === 'fr' ? 'French' : 'English';

  useEffect(() => {
    loadDossier(subProjectId, solutionId);
  }, [subProjectId, solutionId, loadDossier]);

  // Auto-advance to the first incomplete step on load
  useEffect(() => {
    if (!dossier) return;
    const { step_statuses: ss } = dossier;
    const statuses = [ss.step1_brief, ss.step2_bom, ss.step3_make_or_buy, ss.step4_risk_register];
    const firstIncomplete = statuses.findIndex(s => s !== 'completed');
    setActiveStep(firstIncomplete >= 0 ? firstIncomplete : 3);
  }, [dossier]);

  const handleRequestGenerate = useCallback((stepId: StepId) => {
    estimateCost(subProjectId, solutionId);
    setShowCostConfirm(stepId);
  }, [subProjectId, solutionId, estimateCost]);

  const handleConfirmGenerate = useCallback(async (stepId: StepId) => {
    if (!costEstimate) return; // guard: don't fire before estimate arrives
    setShowCostConfirm(null);
    await runStep(subProjectId, solutionId, stepId, language);
  }, [subProjectId, solutionId, language, costEstimate, runStep]);

  const handleCancelGenerate = useCallback(() => setShowCostConfirm(null), []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 text-xs">
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  const ss = dossier?.step_statuses ?? {
    step1_brief: 'not_started', step2_bom: 'not_started',
    step3_make_or_buy: 'not_started', step4_risk_register: 'not_started',
  };
  const stepStatuses = [ss.step1_brief, ss.step2_bom, ss.step3_make_or_buy, ss.step4_risk_register];

  const sharedProps = (stepId: StepId) => ({
    dossier,
    isRunning: isRunningStep && currentStep === stepId,
    costEstimate,
    showCostConfirm: showCostConfirm === stepId,
    onRequestGenerate: () => handleRequestGenerate(stepId),
    onConfirmGenerate: () => handleConfirmGenerate(stepId),
    onCancelGenerate: handleCancelGenerate,
  });

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Step bar */}
      <div className="flex items-center gap-0 border-b border-slate-200 bg-slate-50 flex-shrink-0 px-3 py-0">
        {STEPS.map((step, idx) => (
          <button
            key={step.id}
            onClick={() => setActiveStep(idx)}
            className={`flex items-center px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeStep === idx
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <StepStatusDot status={stepStatuses[idx]} />
            {idx + 1}. {step.label}
          </button>
        ))}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-3 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex-shrink-0">
          {error}
        </div>
      )}

      {/* Step content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {activeStep === 0 && <BriefStep {...sharedProps('step1')} />}
        {activeStep === 1 && <BomStep {...sharedProps('step2')} />}
        {activeStep === 2 && <MakeOrBuyStep {...sharedProps('step3')} />}
        {activeStep === 3 && <RiskRegisterStep {...sharedProps('step4')} />}
      </div>
    </div>
  );
};

export default IndustrializationPane;
